import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { navigation } from '../data/constants';
import type { NavigationItem } from '../types';

interface HeaderProps {
    onLoginClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLoginClick }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <motion.header
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                ? 'bg-white/95 backdrop-blur-md shadow-lg'
                : 'bg-white/90 backdrop-blur-sm shadow-md'
                }`}
        >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="flex items-center space-x-3"
                    >
                        <img
                            src="/images/swarsadhana-logo-animated.svg"
                            alt="SwarSadhana Logo"
                            className="h-10 w-10 lg:h-12 lg:w-12"
                        />
                        <span className="font-cinzel text-xl lg:text-2xl font-bold text-brand-dark-red">
                            SwarSadhana
                        </span>
                    </motion.div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        {navigation.filter(item => item.id !== 'login').map((item: NavigationItem) => (
                            <motion.button
                                key={item.id}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => scrollToSection(item.href.substring(1))}
                                className="font-poppins font-medium transition-colors duration-200 text-gray-700 hover:text-brand-dark-red"
                            >
                                {item.label}
                            </motion.button>
                        ))}
                    </nav>

                    {/* Login Button */}
                    <div className="hidden lg:flex items-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={onLoginClick}
                            className="bg-brand-gold text-gray-900 px-5 py-2 rounded-lg font-poppins font-semibold shadow-lg hover:bg-yellow-500 hover:shadow-xl transition-all duration-300"
                        >
                            Login
                        </motion.button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden p-2 rounded-md"
                    >
                        <div className="w-6 h-6 flex flex-col justify-center items-center">
                            <span
                                className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'
                                    } text-gray-700`}
                            />
                            <span
                                className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                                    } text-gray-700`}
                            />
                            <span
                                className={`block h-0.5 w-6 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'
                                    } text-gray-700`}
                            />
                        </div>
                    </button>
                </div>

                {/* Mobile Menu */}
                <motion.div
                    initial={false}
                    animate={{
                        height: isMobileMenuOpen ? 'auto' : 0,
                        opacity: isMobileMenuOpen ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md rounded-lg mt-2"
                >
                    <div className="py-4 space-y-2">
                        {navigation.filter(item => item.id !== 'login').map((item: NavigationItem) => (
                            <button
                                key={item.id}
                                onClick={() => scrollToSection(item.href.substring(1))}
                                className="block w-full text-left px-4 py-2 text-gray-700 hover:text-brand-dark-red hover:bg-gray-50 font-poppins font-medium transition-colors duration-200"
                            >
                                {item.label}
                            </button>
                        ))}
                        <div className="px-4 pt-2">
                            <button
                                onClick={onLoginClick}
                                className="w-full bg-brand-gold text-gray-900 px-5 py-2 rounded-lg font-poppins font-semibold shadow-lg hover:bg-yellow-500 transition-colors"
                            >
                                Login
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.header>
    );
};

export default Header; 
